



// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters } from 'vuex';

// Third party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Components
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import Input, {
    InputType,
    InputStyle,
    InputSize,
} from '@/shared/components/input.component.vue';
import Tooltip, { TooltipColor } from '@/shared/components/Tooltip.component.vue';
import FlatSelectorText, {
    FlatSelectorTextBorderBottom,
    FlatSelectorTextPredefinedStyle,
    FlatSelectorTextWeight,
} from '@/shared/components/FlatSelectorText.component.vue';
import FloatMenu, {
    FloatMenuPosition,
} from '@/shared/components/float-menu.component.vue';
import MenuElement, {
    MenuElementBgColor,
    MenuElementBgColorHover,
} from '@/shared/components/menu-element.component.vue';

// Models
import { Budget } from '@/manager/budget/models/budget.model';
import {
    BudgetFeeStatus,
    BudgetFee,
    BudgetFeeStatusLiteral,
    BudgetFeePaymentMethod,
    BudgetFeePaymentMethodLiteral,
} from '@/manager/budget/models/budget-fee.model';

// Filters
import formatDate from '@/shared/filters/formatDate.filter.ts';
import round from '@/shared/filters/round.filter.ts';


export enum FeeType {
    Default = 0,
    Reservation = 1,
    SoonPayment = 2,
    MoneyFlow = 3,
}


@Component({
    components: {
        Input,
        Icon,
        FlatSelectorText,
        Tooltip,
        FloatMenu,
        MenuElement,
    },
    computed: {
        ...mapGetters({
            getBudget: 'getBudget',
        }),
    },
    filters: {
        formatDate,
        round,
    },
})
export default class BudgetFeeRow extends Vue {
    @Prop() private budgetId!: string;
    @Prop() private budgetFee!: any;
    @Prop() private editing!: boolean;
    @Prop() private feeNumber!: string;
    @Prop({ default: FeeType.Default }) private feeType!: FeeType;


    private inputType = InputType;
    private inputSize = InputSize;
    private getBudget!: any;
    private iconEnum = IconEnum;
    private flatSelectorTextPredefinedStyle = FlatSelectorTextPredefinedStyle;
    private flatSelectorTextBorderBottom = FlatSelectorTextBorderBottom;
    private flatSelectorTextWeight = FlatSelectorTextWeight;
    private menuElementBgColorHover = MenuElementBgColorHover;
    private menuElementBgColor = MenuElementBgColor;
    private floatMenuPosition = FloatMenuPosition;
    private tooltipColor = TooltipColor;


    @Watch('editing')
    private watchEditing(newVal: boolean, oldVal: boolean) {
        if ( newVal && !this.previousFee ) {
            this.budgetFee.editing = true;
            this.updateBudgetFee();
        }
    }


    get budget() {
        return this.getBudget(this.budgetId);
    }

    get budgetFeePaymentMethodList() {
        const list: any[] = [];

        const enumKeys = Object.keys(BudgetFeePaymentMethod);
        const keysLength = enumKeys.length / 2;
        enumKeys.splice( keysLength, keysLength);
        enumKeys.map((key) => {
            const id = key;
            const literal = BudgetFeePaymentMethodLiteral[Number(id)];
            list.push( { id, literal } );
        });
        return list;
    }

    get tooltipObject() {
        const tooltip = {
            text: '',
            color: TooltipColor.Primary,
            disabled: true,
        };
        if ( this.feeTypeReservation ) {
            tooltip.text = 'Pago inicial';
            tooltip.disabled = false;
        } else if ( this.feeTypeSoonPayment ) {
            tooltip.text = 'Pronto pago';
            tooltip.disabled = false;
        } else if ( this.feeTypeMoneyFlow ) {
            tooltip.text = 'Saldo a favor';
            tooltip.disabled = false;
        }
        return tooltip;
    }

    get statusPending() {
        return this.budgetFee.status === BudgetFeeStatus.Pending;
    }

    get statusOut() {
        return this.budgetFee.status === BudgetFeeStatus.Out;
    }

    get statusPaid() {
        return this.budgetFee.status === BudgetFeeStatus.Paid;
    }

    get statusRemitted() {
        return this.budgetFee.status === BudgetFeeStatus.Remitted;
    }

    get statusReturned() {
        return this.budgetFee.status === BudgetFeeStatus.Returned;
    }

    get statusRecalculated() {
        return this.budgetFee.status === BudgetFeeStatus.Recalculated;
    }

    get previousFee() {
        return this.isPreviousFee(this.budgetFee);
    }

    get feeTooltipText() {
        const tooltip = {
            visible: true,
            text: 'Pendiente',
            color: MenuElementBgColor.BlueDark,
        };
        if ( this.statusPaid ) {
            tooltip.text = BudgetFeeStatusLiteral[BudgetFeeStatus.Paid];
            tooltip.color = MenuElementBgColor.Primary;
        } else if ( this.statusRemitted ) {
            tooltip.text = BudgetFeeStatusLiteral[BudgetFeeStatus.Remitted];
            tooltip.color = MenuElementBgColor.Secondary;
        } else if ( this.statusReturned ) {
            tooltip.text = BudgetFeeStatusLiteral[BudgetFeeStatus.Returned];
            tooltip.color = MenuElementBgColor.Red;
        } else if ( this.statusOut ) {
            tooltip.text = BudgetFeeStatusLiteral[BudgetFeeStatus.Out];
            tooltip.color = MenuElementBgColor.BlueDark;
        } else if ( this.statusRecalculated ) {
            tooltip.text = BudgetFeeStatusLiteral[BudgetFeeStatus.Recalculated];
            tooltip.color = MenuElementBgColor.BlueDark;
        }

        if ( this.feeTypeReservation || this.feeTypeSoonPayment || this.feeTypeMoneyFlow ) {
            tooltip.visible = false;
        }

        return tooltip;
    }

    get feePrice() {
        let price = '';
        if ( this.budgetFee.price !== undefined ) {
            price = this.budgetFee.price.toString();
        }
        return price;
    }

    get showRemoveFee() {
        return this.editing && !this.showFeeIncidence && !this.statusPaid;
    }

    get showFeeIncidence() {
        return this.statusOut || this.statusRecalculated;
    }

    get showFloatMenuOptions() {
        return this.statusReturned && this.editing;
    }

    get hasPaymentDate() {
        return this.budgetFee.paymentDate !== undefined;
    }

    get feeTypeDefault() {
        return this.feeType === FeeType.Default;
    }

    get feeTypeReservation() {
        return this.feeType === FeeType.Reservation;
    }

    get feeTypeSoonPayment() {
        return this.feeType === FeeType.SoonPayment;
    }

    get feeTypeMoneyFlow() {
        return this.feeType === FeeType.MoneyFlow;
    }

    get feeDisabled() {
        return (
            this.previousFee ||
            this.statusOut ||
            this.statusRecalculated ||
            !this.feeTypeDefault
        );
    }


    private updateBudgetFee() {
        this.$store.dispatch('updateStoreBudgetFee', {
                budget: this.budget,
                budgetFee: this.budgetFee,
            });
    }

    private updateBudget() {
        this.$store.dispatch('updateStoreBudget', this.budget);
    }

    private changeStatus() {
        if ( this.editing ) {
            if (this.statusPending) {
                this.budgetFee.status = BudgetFeeStatus.Paid;
            } else if (this.statusPaid) {
                this.budgetFee.status = BudgetFeeStatus.Returned;
            } else if (this.statusReturned) {
                this.budgetFee.status = BudgetFeeStatus.Pending;
            } else if (this.statusOut) {
                this.budgetFee.status = BudgetFeeStatus.Paid;
            } else if (this.statusRemitted) {
                this.budgetFee.status = BudgetFeeStatus.Paid;
            } else if (this.statusRecalculated) {
                this.budgetFee.status = BudgetFeeStatus.Paid;
            }
            this.updateBudgetFee();
        }
    }

    private isPreviousFee(fee: BudgetFee) {
        const now = dayjs();
        const paymentDate = dayjs(fee.paymentDate, { locale: 'es' });
        const remittanceDate = dayjs(fee.remittanceDate, { locale: 'es' });
        return (
            (
                (remittanceDate.diff(now, ( 'days' as UnitType )) < 0) ||
                (paymentDate.diff(now, ( 'days' as UnitType )) < 0)
            ) &&
            !fee.editing
        );
    }

    private removeFee() {
        const budgetFees = this.budget.budgetFee;
        const index = budgetFees.indexOf(this.budgetFee);
        budgetFees.splice( index, 1 );
    }

    private outFee() {
        this.budgetFee.status = BudgetFeeStatus.Out;
    }

    private recalculatedFee() {
        this.budgetFee.status = BudgetFeeStatus.Recalculated;
    }

    private checkOrderFees() {
        this.budget.budgetFee.sort(
            (a: any, b: any) => {
                const aDate = dayjs(a.paymentDate);
                const bDate = dayjs(b.paymentDate);
                if (bDate.isBefore(aDate)) {
                    return 1;
                } else {
                    return -1;
                }
            },
        );
        this.updateBudget();
    }

    private recalculateNextFeePrice() {
        if ( !this.statusOut && !this.statusRecalculated ) {
            const budgetFees = this.budget.budgetFee;
            const index = budgetFees.indexOf(this.budgetFee);
            let feesWithoutCurrent = budgetFees.slice(index + 1, budgetFees.length + 1);
            feesWithoutCurrent = feesWithoutCurrent.filter((fee: any) => {
                return (
                    fee.status !== BudgetFeeStatus.Recalculated &&
                    fee.status !== BudgetFeeStatus.Out
                );
            });
            const total = budgetFees.reduce(
                (total: number, fee: any) => {
                    if (
                        fee.status !== BudgetFeeStatus.Recalculated &&
                        fee.status !== BudgetFeeStatus.Out
                     ) {
                        return total + Number(fee.price);
                    } else {
                        return total;
                    }
                },
                0,
            );
            const diff = total - this.budget.totalPrice;
            const diffFee = diff / feesWithoutCurrent.length;
            let amountFee = 0;
            if ( diff < 0 ) {
                amountFee = Math.abs(diffFee);
            } else if ( diff > 0 ) {
                amountFee = -1 * (diffFee);
            }
            feesWithoutCurrent.map((fee: any) => {
                const feePrice =  Number(fee.price) || 0;
                let newPrice = Math.round( (feePrice + amountFee) * 100) / 100;
                if ( newPrice < 0 ) {
                    newPrice = 0;
                }
                fee.price = newPrice;
            });
        }
    }

    private recalculate() {
        this.recalculatedFee();
        const recalculateFees: any = [];
        let feeTotalPaid = 0;
        this.budget.budgetFee.forEach((fee: any) => {
            if (
                !this.isPreviousFee(fee) &&
                fee.status !== BudgetFeeStatus.Out &&
                fee.status !== BudgetFeeStatus.Recalculated &&
                fee.status !== BudgetFeeStatus.Paid
            ) {
                recalculateFees.push(fee);
            } else if (
                fee.status === BudgetFeeStatus.Paid ||
                this.isPreviousFee(fee)
            ) {
                feeTotalPaid += fee.price;
            }
        });
        const NotPaidFeesLength = recalculateFees.length;
        const amountPriceFee = this.budgetFee.price / NotPaidFeesLength;
        let totalPaid = 0;
        recalculateFees.forEach((fee: any) => {
            const feePrice = Number(fee.price) || 0;
            const totalPrice = Math.round( (amountPriceFee + feePrice) * 100) / 100;
            fee.price = totalPrice;
            totalPaid += totalPrice;
        });
        totalPaid += feeTotalPaid;
        if ( totalPaid < this.budget.totalPendingPrice ) {
            const diff = this.budget.totalPendingPrice - totalPaid;
            const lastFee = recalculateFees[NotPaidFeesLength - 1];
            lastFee.price = Math.round( (lastFee.price + diff) * 100) / 100;
        }
    }

}

